import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Input, InputProps } from '@dtpk-cc/components';

type RequiredFromInputProps = Required<Pick<InputProps, 'name' | 'id' | 'children'>>;
type OmitFromInputProps = Omit<InputProps, 'onChange' | 'required'>;
type ControlledInputProps = OmitFromInputProps & RequiredFromInputProps;

export const ControlledInput = ({ id, name, children, ...inputProps }: ControlledInputProps) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
        control,
        name,
        defaultValue: '',
    });

    const customRef = useCallback(
        (inputDiv: HTMLInputElement) => {
            // The Living Production Guide's Input component doesn't put its ref on the input element, but on the
            // surrounding div (.Input_wrapper...).
            // That's why React Form Hook's focus handling and other stuff doesn't work the way it's supposed to,
            // since it expects an <input> to call .focus() on and not a <div>.
            // So what we try to do here is find the Input_wrapper's corresponding <input> and pass it to
            // React Form Hook
            const realInputElement = (inputDiv as HTMLDivElement)?.getElementsByTagName('input')[0];
            return field.ref(realInputElement);
        },
        [field]
    );

    return (
        <Input
            id={id}
            name={field.name}
            value={field.value}
            ref={customRef}
            isValid={!fieldState.invalid}
            invalidMessage={fieldState.error?.message}
            onChange={field.onChange}
            onBlur={field.onBlur}
            {...inputProps} // eslint-disable-line react/jsx-props-no-spreading
        >
            {children}
        </Input>
    );
};
